import React from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { Box, Separator, Tag, Text, Tooltip } from '@betterment/samba-react';
import { HtmlContent } from '@betterment/shared-components';

import { AccountOverviewEnvelopeAccountQuery } from './graphql/query.envelope.generated';

interface CashAccountDetailsProps {
  accountData: AccountOverviewEnvelopeAccountQuery['envelopeAccount'] & { __typename: 'CashAccount' };
}

const DetailsContainer = ({
  children,
  header,
  explanation,
}: {
  children: React.ReactNode;
  header: string;
  explanation?: DocumentFragment | string;
}) => (
  <Box css={{ display: 'flex', flexDirection: 'column' }}>
    <Box>
      <Text variant="text50" color="text.weak" css={{ mr: 'space.50' }}>
        {header}
      </Text>
      {explanation != null && (
        <Tooltip>
          <Tooltip.Trigger asChild={true}>
            <Tooltip.IconButton />
          </Tooltip.Trigger>
          <Tooltip.Content align="center" side="top">
            <Tooltip.Text>
              {explanation instanceof DocumentFragment ? (
                <HtmlContent documentFragment={explanation} as="span" />
              ) : (
                explanation
              )}
            </Tooltip.Text>
          </Tooltip.Content>
        </Tooltip>
      )}
    </Box>
    <Text>{children}</Text>
  </Box>
);

export const CashAccountDetails: React.FC<CashAccountDetailsProps> = ({ accountData }) => {
  const zeroInterest = toMoney(0);
  return (
    <Box>
      <Box css={{ display: 'flex', gap: 'space.300', justifyContent: 'space-between' }}>
        <DetailsContainer header="Current APY*" explanation={accountData.apy.explanation}>
          <Text variant="text200">{accountData.apy.rate}%</Text>
          {accountData.apy.isBoostEligible && accountData.apy.isBoostActive && (
            <Tag css={{ ml: 'space.100' }} accent="teal">
              Boosted
            </Tag>
          )}
        </DetailsContainer>
        <DetailsContainer header="FDIC Insurance" explanation={accountData.fdicInsurance.explanation}>
          <Text variant="text200">{accountData.fdicInsurance.amount}</Text>
        </DetailsContainer>
        <DetailsContainer header="Total interest paid">
          <Text variant="text200" color="text.success">
            {formatMoney(accountData.performance.totals.interest?.paid || zeroInterest)}
          </Text>
        </DetailsContainer>
        <DetailsContainer
          header="Interest to be paid"
          explanation={accountData.performance.totals.interest?.accruedExplanation}
        >
          <Text variant="text200" color="text.success">
            {formatMoney(accountData.performance.totals.interest?.accrued || zeroInterest)}
          </Text>
        </DetailsContainer>
      </Box>
      <Separator css={{ my: 'space.300' }} />
    </Box>
  );
};
